import { useState, type ReactNode } from "react";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  DatePickerWithRange,
  perkDatePresetRange,
} from "@/components/ui/date-range-picker";
import {
  downloadAuditHistoryReport,
  downloadCatalogReport,
  downloadCsvFile,
  downloadCustomerActivityReport,
  downloadPointExpiryReport,
  getReportErrorMessage,
  reportFileName,
  type CatalogReportId,
  type CsvTable,
} from "./report/reportDownloads";
import {
  BreakageIllustration,
  CampaignIllustration,
  EnrolmentIllustration,
  IssuedBySourceIllustration,
  LedgerIllustration,
  LiabilityIllustration,
  MemberActivityIllustration,
  ProgramPerformanceIllustration,
  RedemptionIllustration,
  RuleCostIllustration,
  SegmentMembersIllustration,
  SegmentSnapshotIllustration,
  TierMovementIllustration,
} from "./report/reportIllustrations";

type ReportDefinition = {
  id: string;
  title: string;
  description: string;
  fileSlug: string;
  grain?: string;
  cadence?: string;
  illustration?: ReactNode;
  download: (range: DateRange) => Promise<CsvTable>;
};

function catalogReport(
  id: CatalogReportId,
  title: string,
  description: string,
  grain: string,
  cadence: string,
  illustration: ReactNode,
): ReportDefinition {
  return {
    id,
    title,
    description,
    fileSlug: id,
    grain,
    cadence,
    illustration,
    download: (range) => downloadCatalogReport(id, range),
  };
}

function CustomerActivityIllustration() {
  return (
    <svg viewBox="0 0 320 148" className="h-full w-full" aria-hidden="true">
      <rect width="320" height="148" fill="#ecfdf5" />
      <circle cx="278" cy="26" r="52" fill="#a7f3d0" />
      <circle cx="28" cy="132" r="36" fill="#99f6e4" />
      <rect x="28" y="22" width="156" height="108" rx="16" fill="#ffffff" />
      <circle cx="54" cy="50" r="12" fill="#34d399" />
      <rect x="76" y="44" width="82" height="7" rx="3.5" fill="#d1fae5" />
      <rect x="76" y="56" width="48" height="5" rx="2.5" fill="#a7f3d0" />
      <circle cx="54" cy="82" r="12" fill="#14b8a6" />
      <rect x="76" y="76" width="70" height="7" rx="3.5" fill="#d1fae5" />
      <rect x="76" y="88" width="40" height="5" rx="2.5" fill="#a7f3d0" />
      <circle cx="54" cy="112" r="12" fill="#059669" />
      <rect x="76" y="106" width="88" height="7" rx="3.5" fill="#d1fae5" />
      <rect x="200" y="34" width="96" height="84" rx="16" fill="#ffffff" />
      <rect x="216" y="82" width="12" height="20" rx="3" fill="#6ee7b7" />
      <rect x="236" y="66" width="12" height="36" rx="3" fill="#34d399" />
      <rect x="256" y="50" width="12" height="52" rx="3" fill="#059669" />
      <path
        d="M214 78c10-16 22-14 32-24 8-8 16-8 26-2"
        fill="none"
        stroke="#0f766e"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PointExpiryIllustration() {
  return (
    <svg viewBox="0 0 320 148" className="h-full w-full" aria-hidden="true">
      <rect width="320" height="148" fill="#fffbeb" />
      <circle cx="40" cy="24" r="40" fill="#fde68a" />
      <circle cx="286" cy="126" r="44" fill="#fcd34d" opacity="0.7" />
      <rect x="36" y="28" width="132" height="100" rx="16" fill="#ffffff" />
      <rect x="36" y="28" width="132" height="28" rx="16" fill="#f59e0b" />
      <rect x="36" y="44" width="132" height="12" fill="#f59e0b" />
      <circle cx="62" cy="42" r="4" fill="#fffbeb" />
      <circle cx="142" cy="42" r="4" fill="#fffbeb" />
      <rect x="54" y="70" width="18" height="14" rx="3" fill="#fef3c7" />
      <rect x="80" y="70" width="18" height="14" rx="3" fill="#fef3c7" />
      <rect x="106" y="70" width="18" height="14" rx="3" fill="#f59e0b" />
      <rect x="132" y="70" width="18" height="14" rx="3" fill="#fef3c7" />
      <rect x="54" y="92" width="18" height="14" rx="3" fill="#fef3c7" />
      <rect x="80" y="92" width="18" height="14" rx="3" fill="#fde68a" />
      <rect x="106" y="92" width="18" height="14" rx="3" fill="#fef3c7" />
      <circle cx="214" cy="78" r="40" fill="#ffffff" />
      <circle cx="214" cy="78" r="28" fill="#fef3c7" />
      <text
        x="214"
        y="84"
        textAnchor="middle"
        fill="#b45309"
        fontSize="18"
        fontWeight="700"
        fontFamily="inherit"
      >
        PTS
      </text>
      <path
        d="M248 46c14 8 22 24 20 42"
        fill="none"
        stroke="#d97706"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="4 6"
      />
    </svg>
  );
}

function AuditHistoryIllustration() {
  return (
    <svg viewBox="0 0 320 148" className="h-full w-full" aria-hidden="true">
      <rect width="320" height="148" fill="#eef2ff" />
      <circle cx="274" cy="30" r="48" fill="#c7d2fe" />
      <circle cx="30" cy="124" r="34" fill="#ddd6fe" />
      <rect x="48" y="36" width="28" height="88" rx="6" fill="#c7d2fe" />
      <rect x="64" y="24" width="140" height="108" rx="14" fill="#ffffff" />
      <rect x="82" y="42" width="72" height="8" rx="4" fill="#e0e7ff" />
      <circle cx="88" cy="68" r="6" fill="#4f46e5" />
      <rect x="102" y="64" width="78" height="6" rx="3" fill="#e0e7ff" />
      <circle cx="88" cy="88" r="6" fill="#818cf8" />
      <rect x="102" y="84" width="64" height="6" rx="3" fill="#e0e7ff" />
      <circle cx="88" cy="108" r="6" fill="#a5b4fc" />
      <rect x="102" y="104" width="84" height="6" rx="3" fill="#e0e7ff" />
      <circle cx="230" cy="92" r="28" fill="#ffffff" />
      <circle
        cx="230"
        cy="92"
        r="16"
        fill="none"
        stroke="#4f46e5"
        strokeWidth="4"
      />
      <path
        d="M242 106l16 16"
        stroke="#312e81"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Add a report by appending one object: title, description, illustration, and download.
 * The date range on this page is passed into `download`.
 */
const REPORTS: ReportDefinition[] = [
  {
    id: "customer-activity",
    title: "Customer Activity Report",
    description:
      "Member transactions, invoices, and earn or burn activity.",
    fileSlug: "customer-activity-report",
    illustration: <CustomerActivityIllustration />,
    download: downloadCustomerActivityReport,
  },
  {
    id: "point-expiry",
    title: "Point Expiry Report",
    description: "Points that expired for members, with outlet and date.",
    fileSlug: "point-expiry-report",
    illustration: <PointExpiryIllustration />,
    download: downloadPointExpiryReport,
  },
  {
    id: "audit-history",
    title: "Audit History Report",
    description: "Program changes, approvals, and other important actions.",
    fileSlug: "audit-history-report",
    illustration: <AuditHistoryIllustration />,
    download: downloadAuditHistoryReport,
  },
];

const REPORT_GROUPS: { title: string; reports: ReportDefinition[] }[] = [
  {
    title: "Program performance",
    reports: [
      catalogReport(
        "program-performance",
        "Program performance summary",
        "Members, new, active, repeat rate, bookings and value from members, the points economy, and tier mix. This is the dashboard summary.",
        "Month",
        "Monthly, emailed to owner",
        <ProgramPerformanceIllustration />,
      ),
      REPORTS[0],
      catalogReport(
        "member-activity",
        "Member activity",
        "Per member: tier, balance, RFM scores, segment, lifetime spend, and last booking.",
        "Member",
        "On demand",
        <MemberActivityIllustration />,
      ),
      catalogReport(
        "tier-movement",
        "Tier movement",
        "Upgrades, downgrades, manual changes, members close to the next tier, and downgrades coming up.",
        "Tier",
        "Monthly",
        <TierMovementIllustration />,
      ),
    ],
  },
  {
    title: "Marketing / CRM",
    reports: [
      catalogReport(
        "campaign-performance",
        "Campaign performance",
        "Sent, delivered, read, clicked, converted, lift versus control, booking value, points cost, and cost per conversion, by channel. Includes a monthly roll-up.",
        "Campaign",
        "Per campaign and monthly roll-up",
        <CampaignIllustration />,
      ),
      catalogReport(
        "enrolment",
        "Enrolment report",
        "New members by source — organic, campaign, invite code, and import — and invite code usage.",
        "Day",
        "Weekly",
        <EnrolmentIllustration />,
      ),
      catalogReport(
        "segment-snapshot",
        "Segment snapshot",
        "Count per lifecycle and custom segment, and movement between segments.",
        "Segment",
        "Monthly",
        <SegmentSnapshotIllustration />,
      ),
      catalogReport(
        "segment-members",
        "Segment member list",
        "Member IDs in a segment, for use in a CRM or ad tool.",
        "Member",
        "On demand",
        <SegmentMembersIllustration />,
      ),
    ],
  },
  {
    title: "Finance",
    reports: [
      catalogReport(
        "points-liability",
        "Points liability statement",
        "Opening balance, issued, redeemed, expired, reversed, adjusted, and closing balance, in points and money. Pending and available are shown separately.",
        "Month",
        "Monthly, auto-generated on the 1st",
        <LiabilityIllustration />,
      ),
      catalogReport(
        "points-by-source",
        "Points issued by source",
        "Points and cost by rule, campaign, tier benefit, invite code, and admin adjustment.",
        "Month × source",
        "Monthly",
        <IssuedBySourceIllustration />,
      ),
      catalogReport(
        "redemption",
        "Redemption report",
        "Points burned, their money value, and what they were spent on.",
        "Day",
        "Weekly",
        <RedemptionIllustration />,
      ),
      catalogReport(
        "breakage-forecast",
        "Breakage and expiry forecast",
        "Points expiring in the next 30, 60, and 90 days, with actual expiry against the forecast.",
        "Month",
        "Monthly",
        <BreakageIllustration />,
      ),
      REPORTS[1],
      catalogReport(
        "rule-cost",
        "Rule / offer cost",
        "Points and money per rule, members affected, and budget used.",
        "Rule",
        "Monthly",
        <RuleCostIllustration />,
      ),
    ],
  },
  {
    title: "Compliance",
    reports: [
      REPORTS[2],
      catalogReport(
        "ledger-detail",
        "Ledger detail export",
        "Every ledger entry with member ID, type, points, source, and booking reference.",
        "Entry",
        "On demand, date-ranged",
        <LedgerIllustration />,
      ),
    ],
  },
];

function getDefaultDateRange(): DateRange {
  return perkDatePresetRange("last30");
}

export default function Report() {
  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    getDefaultDateRange,
  );
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const rangeLabel =
    dateRange?.from && dateRange.to
      ? `${format(dateRange.from, "MMM d, yyyy")} – ${format(dateRange.to, "MMM d, yyyy")}`
      : "Select a start and end date";

  const handleDownload = async (report: ReportDefinition) => {
    if (!dateRange?.from || !dateRange.to) {
      toast.error("Select a start and end date before downloading.");
      return;
    }
    if (downloadingId) return;

    setDownloadingId(report.id);
    try {
      const table = await report.download(dateRange);
      if (table.rows.length === 0) {
        toast.error(`No records in ${rangeLabel} for ${report.title}.`);
        return;
      }
      downloadCsvFile(reportFileName(report.fileSlug, dateRange), table);
      toast.success(`${report.title} downloaded.`);
    } catch (error) {
      toast.error(
        getReportErrorMessage(error, `Could not download ${report.title}.`),
      );
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Report
          </h1>
          <p className="mt-1 text-muted-foreground">
            Pick a report to download it as a CSV for the selected dates.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-medium text-slate-900">Date range</p>
          <p className="text-sm text-muted-foreground">
            Every download uses {rangeLabel}.
          </p>
        </div>
        <DatePickerWithRange
          presets
          date={dateRange}
          setDate={setDateRange}
          placeholder="Select date range"
          className="w-full sm:w-72"
        />
      </div>

      {REPORT_GROUPS.map((group) => (
        <section key={group.title} className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">
            {group.title}
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {group.reports.map((report) => {
              const isDownloading = downloadingId === report.id;
              return (
                <button
                  key={report.id}
                  type="button"
                  onClick={() => void handleDownload(report)}
                  disabled={downloadingId !== null}
                  aria-busy={isDownloading}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:cursor-wait disabled:hover:translate-y-0"
                >
                  <div className="h-36 overflow-hidden border-b border-slate-100">
                    {report.illustration}
                  </div>
                  <div className="flex flex-1 flex-col gap-2 p-5">
                    <h3 className="text-base font-semibold text-slate-900">
                      {report.title}
                    </h3>
                    <p className="text-sm leading-5 text-muted-foreground">
                      {report.description}
                    </p>
                    {report.grain && report.cadence ? (
                      <div className="mt-auto flex items-center justify-between gap-3 pt-2">
                        <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                          {report.grain}
                        </span>
                        <span className="text-right text-xs text-slate-500">
                          {report.cadence}
                        </span>
                      </div>
                    ) : null}
                  </div>
                  <div className="mt-auto flex items-center justify-between border-t border-slate-100 px-5 py-3 text-sm font-medium text-slate-700">
                    <span>{isDownloading ? "Preparing CSV…" : "Download CSV"}</span>
                    {isDownloading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 transition group-hover:translate-y-0.5" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
